import { useEffect, useState } from "react";
import { api, formatBRL, formatDateBR } from "../../lib/api";
import { Users, TrendingUp, DollarSign, Activity, Building2 } from "lucide-react";

export default function SaaSAdmin() {
  const [metrics, setMetrics] = useState(null);
  const [tenants, setTenants] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/saas/admin/metrics").then(r => setMetrics(r.data)),
      api.get("/saas/admin/tenants").then(r => setTenants(r.data)),
    ]).catch(() => {});
  }, []);

  if (!metrics) return <div className="text-gray-500">Carregando…</div>;

  const cards = [
    { label: "Total de tenants", value: metrics.total_tenants, icon: Building2 },
    { label: "Trials ativos", value: metrics.trials_active, icon: Activity, accent: "#F59E0B" },
    { label: "Assinantes pagantes", value: metrics.subscriptions_active, icon: Users, accent: "#10B981" },
    { label: "MRR (Receita SaaS)", value: formatBRL(metrics.mrr_brl), icon: DollarSign, accent: "#D4AF37" },
    { label: "ARPU", value: formatBRL(metrics.arpu_brl), icon: TrendingUp },
    { label: "Conversão", value: `${metrics.conversion_rate_pct}%`, icon: TrendingUp },
    { label: "Signups (30d)", value: metrics.signups_30d, icon: Users },
    { label: "Churn", value: metrics.churned, icon: Activity, accent: "#EF4444" },
  ];

  return (
    <div data-testid="saas-admin">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.18em] text-[#D4AF37] font-semibold mb-2">Super Admin · SaaS</div>
        <h1 className="font-display text-3xl text-[#14213D] font-light">Métricas do SaaS</h1>
        <p className="text-gray-500 text-sm mt-1">Visão consolidada de todos os tenants do VM Corporate Software.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {cards.map((c) => (
          <div key={c.label} className="kpi-card" data-testid={`saas-kpi-${c.label.replace(/\s/g, '-').toLowerCase()}`}>
            <c.icon size={16} className="mb-3" style={{ color: c.accent || "#14213D" }} />
            <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">{c.label}</div>
            <div className="font-display text-2xl text-[#14213D] mt-1">{c.value}</div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-display text-xl text-[#14213D]">Tenants</h2>
          <span className="text-xs text-gray-400">{tenants.length} cadastrados</span>
        </div>
        <div className="table-wrap">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500 font-semibold">
            <tr>
              <th className="px-5 py-3">Empresa</th>
              <th className="px-5 py-3">Responsável</th>
              <th className="px-5 py-3">Plano</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Trial até</th>
              <th className="px-5 py-3">Criado</th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((t) => (
              <tr key={t.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`tenant-row-${t.slug}`}>
                <td className="px-5 py-3">
                  <div className="font-semibold text-[#14213D]">{t.company_name}</div>
                  <div className="text-[10px] text-gray-400">{t.slug}</div>
                </td>
                <td className="px-5 py-3 text-gray-600">
                  <div>{t.owner_name}</div>
                  <div className="text-xs text-gray-400">{t.owner_email}</div>
                </td>
                <td className="px-5 py-3">
                  <span className="text-[11px] px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold bg-blue-50 text-blue-700">{t.plan}</span>
                </td>
                <td className="px-5 py-3">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold ${
                    t.status === "active" ? "bg-emerald-50 text-emerald-700" :
                    t.status === "trialing" ? "bg-amber-50 text-amber-700" :
                    "bg-red-50 text-red-700"
                  }`}>{t.status}</span>
                </td>
                <td className="px-5 py-3 text-xs text-gray-500">{t.trial_ends_at ? formatDateBR(t.trial_ends_at) : "—"}</td>
                <td className="px-5 py-3 text-xs text-gray-500">{formatDateBR(t.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
